<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { HousingOrgTypes } from "~/api";
import TreeSelector from "~/components/TreeSelector.vue";
import { useGeoTree } from "~/composables/useGeoTree";
import { useHousingOrgStore } from "../../stores/HousingOrgStore";
import { ACCESSIBLE_LEGACY_ATTR_GUID } from "../../utils/constants";
import {
  HousingFilters,
  LocationInfo,
  QuadrantInfo,
  RuleForm,
} from "../types/plusTypes";
import CalculatorCurrentInputs from "./CalculatorCurrentInputs.vue";
import QuadrantLocationTree from "./QuadrantLocationTree.vue";

const housingOrgStore = useHousingOrgStore();

const props = defineProps<{
  surveyCompleted: boolean;
  ruleForm: RuleForm;
  quadrants: QuadrantInfo[];
  locations: LocationInfo[];
  validLocationNames: string[];
  validLocationGuids: string[];
  sortingList: string[];
  roomOptions: { label: string; value: string }[];
  buildingOptions: { name: string; guid: string }[];
  buildingAttributeOptions: { name: string; guid: string }[];
  applicationInProgress: boolean;
  isReadOnly?: boolean;
  selectedSectorLabels?: string[];
}>();

const localBuildingAttributeOptions = computed(() => {
  const accessibleLegacyAttrGuidLower =
    ACCESSIBLE_LEGACY_ATTR_GUID.toLowerCase();

  // start with the list from props.
  // if buildingAttributeChecklist contains ACCESSIBLE_LEGACY_ATTR_GUID add it to the list of options from the BuildingAttribute tree node
  const options = [...props.buildingAttributeOptions];
  const normalizedBuildingAttributeChecklist =
    filters.value.buildingAttributeChecklist?.map((guid) => guid.toLowerCase());
  if (
    normalizedBuildingAttributeChecklist?.includes(
      accessibleLegacyAttrGuidLower,
    ) &&
    !options.some(
      (option) => option.guid.toLowerCase() === accessibleLegacyAttrGuidLower,
    )
  ) {
    options.push({
      guid: ACCESSIBLE_LEGACY_ATTR_GUID,
      name: "Accessible Housing (Legacy)",
    });
  }
  return options;
});

const filters = defineModel<HousingFilters>({ required: true });

const emit = defineEmits<{
  (e: "update:surveyCompleted", value: boolean): void;
  (e: "clearFilters"): void;
}>();

// ---- Geo Settings Tree ----
const { geoTreeDefinitionGuid, flatNodeMap, loadGeoTree } = useGeoTree();
const geoSelectedGuids = ref<string[]>(filters.value.locationCheckList || []);

// Build a GUID → count map from validLocationGuids for the geo tree
const geoNodeCounts = computed<Map<string, number>>(() => {
  const counts = new Map<string, number>();
  if (!flatNodeMap.value.size) return counts;

  // Count occurrences of each geo tree node GUID
  const guidCounts = new Map<string, number>();
  for (const guid of props.validLocationGuids) {
    guidCounts.set(guid, (guidCounts.get(guid) || 0) + 1);
  }

  // Assign counts to nodes by GUID, then roll up to ancestors
  for (const [guid, count] of guidCounts) {
    const node = flatNodeMap.value.get(guid);
    if (node) {
      counts.set(guid, (counts.get(guid) || 0) + count);
      // Walk up to ancestors and accumulate
      let parentGuid = node.parentTreeNodeGuid;
      while (parentGuid) {
        counts.set(parentGuid, (counts.get(parentGuid) || 0) + count);
        const parent = flatNodeMap.value.get(parentGuid);
        parentGuid = parent?.parentTreeNodeGuid ?? null;
      }
    }
  }
  return counts;
});

// When tree selections change, map GUIDs back to location name strings
// so filters.locationCheckList stays compatible with Finder.vue's filterResults()
const handleGeoSelectionChange = (guids: string[]) => {
  geoSelectedGuids.value = guids;
  filters.value.locationCheckList = guids;
};

const showAllBuildingTypes = computed<boolean>({
  // if no filters are applied 'All' checkbox should be checked
  get: () => (filters.value.buildingTypeChecklist?.length ?? 0) === 0,
  // if 'All' checkbox is checked, clear buildingTypeChecklist
  set: (value) => {
    if (value) {
      filters.value.buildingTypeChecklist = [];
    }
  },
});

const showAllLocations = computed<boolean>({
  // if no filters are applied 'All' checkbox should be checked
  get: () => filters.value.locationCheckList.length === 0,
  // if 'All' checkbox is checked, clear locationCheckList
  set: (value) => {
    if (value) {
      filters.value.locationCheckList = [];
      geoSelectedGuids.value = [];
    }
  },
});

const showAllHMBs = computed<boolean>({
  // if no filters are applied 'All' checkbox should be checked
  get: () => filters.value.housingOrgChecklist.length === 0,
  // if 'All' checkbox is checked, clear locationCheckList
  set: (value) => {
    if (value) {
      filters.value.housingOrgChecklist = [];
    }
  },
});

const filterApplied = computed<boolean>(() => {
  return (
    filters.value.housingOrgChecklist.length > 0 ||
    filters.value.locationCheckList.length > 0 ||
    (filters.value.buildingTypeChecklist?.length ?? 0) > 0 ||
    (filters.value.buildingAttributeChecklist?.length ?? 0) > 0 ||
    filters.value.accessibleOnly
  );
});

const sortedRoomOptions = computed(() => {
  // Studio first, otherwise alphabetical by label.
  return [...props.roomOptions].sort((a, b) => {
    if (a.label === "Studio") return -1;
    if (b.label === "Studio") return 1;
    return a.label.localeCompare(b.label);
  });
});

// Refresh filters on update of location checklist (prevent reactivity issues)
watch(
  () => filters.value.locationCheckList,
  () => (filters.value = { ...filters.value }),
);

onMounted(async () => {
  // default to all room types if none are selected
  if (!filters.value.roomTypeChecklist.length) {
    filters.value.roomTypeChecklist = sortedRoomOptions.value.map(
      (option) => option.value,
    );
  }
  await loadGeoTree();
});
</script>
<template>
  <div class="filterBox">
    <CalculatorCurrentInputs
      :survey-completed="surveyCompleted"
      :rule-form="ruleForm"
      :is-read-only="isReadOnly"
      :selected-sector-labels="props.selectedSectorLabels"
      @update:survey-completed="
        (value: boolean) => emit('update:surveyCompleted', value)
      "
    />

    <hr style="width: 100%" />

    <div class="filterGroup">
      <div class="heading">
        <h4>Building Types</h4>
        <el-checkbox
          v-model="showAllBuildingTypes"
          label="All"
          :disabled="isReadOnly"
        />
      </div>
      <span v-if="buildingOptions.length === 0" class="roomOptionMessage">
        None Available
      </span>
      <div class="roomOptions">
        <el-checkbox-group
          v-model="filters.buildingTypeChecklist"
          class="filterGroupInner"
          :disabled="isReadOnly"
        >
          <el-checkbox
            v-for="option in buildingOptions"
            :key="option.guid"
            :label="option.name"
            :value="option.guid"
          ></el-checkbox>
        </el-checkbox-group>
      </div>
    </div>

    <div class="filterGroup">
      <div class="heading">
        <h4>Attributes</h4>
      </div>
      <span
        v-if="localBuildingAttributeOptions.length === 0"
        class="roomOptionMessage"
      >
        None Available
      </span>
      <el-checkbox-group
        v-model="filters.buildingAttributeChecklist"
        class="filterGroupInner"
        :disabled="isReadOnly"
      >
        <el-checkbox
          v-for="option in localBuildingAttributeOptions"
          :key="option.guid"
          :label="option.name"
          :value="option.guid"
        />
      </el-checkbox-group>
    </div>

    <div class="filterGroup">
      <div class="heading">
        <h4>Room Options</h4>
      </div>
      <span v-if="roomOptions.length === 0" class="roomOptionMessage">
        None Available
      </span>
      <div class="roomOptions">
        <el-checkbox-group
          v-model="filters.roomTypeChecklist"
          class="filterGroupInner"
          :disabled="isReadOnly"
        >
          <el-checkbox
            v-for="option in sortedRoomOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          ></el-checkbox>
        </el-checkbox-group>
        <el-checkbox
          v-model="filters.accessibleOnly"
          class="filterGroupInner"
          :disabled="isReadOnly"
          style="margin-top: 8px"
        >
          Accessible Unit
        </el-checkbox>
      </div>
    </div>

    <div class="filterGroup">
      <div class="heading">
        <h4>Location</h4>
        <el-checkbox
          v-model="showAllLocations"
          label="All"
          :disabled="isReadOnly"
        />
      </div>
      <!-- Use admin-configured Geo Settings tree when available -->
      <TreeSelector
        v-if="geoTreeDefinitionGuid"
        :tree-definition-guid="geoTreeDefinitionGuid"
        :model-value="geoSelectedGuids"
        :show-checkbox="true"
        :multiple="true"
        :show-level-name="false"
        :default-expanded="false"
        :node-counts="geoNodeCounts"
        :disable-zero-counts="true"
        root-node-name="Calgary"
        :disabled="isReadOnly"
        @update:model-value="handleGeoSelectionChange"
      />
      <!-- Fallback to hardcoded QuadrantLocationTree when no tree configured -->
      <QuadrantLocationTree
        v-else
        v-model:selected-locations="filters.locationCheckList"
        :locations="locations"
        :quadrants="quadrants"
        :valid-location-names="validLocationNames"
        :disabled="isReadOnly"
      />
    </div>

    <div class="filterGroup">
      <div class="heading">
        <h4>Housing Organization</h4>
        <el-checkbox v-model="showAllHMBs" label="All" :disabled="isReadOnly" />
      </div>
      <el-checkbox-group
        v-model="filters.housingOrgChecklist"
        class="filterGroupInner"
        :disabled="isReadOnly"
      >
        <el-checkbox
          v-for="org in housingOrgStore.List.filter(
            (org) => org.type === HousingOrgTypes.HMB,
          )"
          :key="org.code"
          :label="org.name"
          :value="org.code"
        ></el-checkbox>
      </el-checkbox-group>
    </div>

    <!-- v-if="resultsFiltered.length !== results.length" -->
    <a
      v-if="filterApplied"
      type="info"
      class="resetFilterBtn"
      @click="
        geoSelectedGuids = [];
        emit('clearFilters');
      "
      >Reset Filters</a
    >
  </div>
</template>
<style lang="less">
.filterBox {
  display: grid;
  gap: 1em;

  .el-checkbox.el-checkbox--small .el-checkbox__inner::after {
    left: 3px;
    top: 0px;
  }

  .el-checkbox {
    color: black;
  }

  .roomOptionsHeading {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 5px;
  }
  .roomOptions {
    margin-bottom: 5px;
  }

  .roomOptionMessage {
    color: var(--el-text-color-regular);
    font-weight: 450;
    font-size: 18px;
    font-style: italic;

    .tooltipButton {
      margin-top: -5px;
    }
  }

  .locationFilters {
    margin-bottom: 8px;
  }

  .filterGroup {
    .heading {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1em;
      h4 {
        font-weight: bold;
        margin: 0;
      }
    }
    .el-checkbox-group {
      display: grid;
    }
    .filterGroupInner {
      margin-left: 29px; //line up exactly with the location quadrant checkboxes
    }
  }

  .resetFilterBtn {
    cursor: pointer;
    text-decoration: underline;
    margin-top: 1em;
  }
  @media (max-width: 845px) {
    padding-top: 1em;
  }
}

.el-popover.el-popper {
  --yych-background-color-light: #f5f5f5;

  background-color: var(--yych-background-color-light);
  .el-popover__title {
    font-size: 1.2rem;
    color: #000;
    margin: 0.25em 0.5em;
    line-height: 1.5;
    background-color: var(--yych-background-color-light);
  }
}
</style>
